import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, DatePicker, Popconfirm } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { ExportToCsv } from 'export-to-csv';
import commonMethod from '../../lib/commonMethod';
import cookie from 'react-cookies';
import Logo from '../../assets/img/logo.svg';

const { RangePicker } = DatePicker;

function MotionDoorHistory({
  deviceId,
  deviceName,
  deviceType,
  history: routeHistory
}) {
  const dispatch = useDispatch();
  const accessToken = useMemo(() => cookie.load('accessToken'), []);
  const { history, startAt, endAt, loading } = useSelector(
    state => state.history
  );

  const { isRefresh } = useSelector(state => state.hejhome);

  useEffect(() => {
    const startAt = moment().subtract(1, 'week').add(1, 'day').startOf('day');
    const endAt = moment().endOf('day');
    dispatch({ type: 'setLoading', loading: true });
    dispatch({ type: 'setDate', startAt, endAt });
  }, [dispatch]);

  const historyResolved = useMemo(() => {
    const historyMap = (history || []).reduceRight((prev, data) => {
      if (data.code === 'pir' && data.value === 'none') {
        return prev;
      }
      const date = moment(data.t).format('YYYY년 MM월 DD일');
      prev[date] = prev[date] || {
        date,
        list: []
      };
      prev[date]['list'].push(data);
      return prev;
    }, {});
    return Object.values(historyMap);
  }, [history]);

  const disabledDate = useCallback(current => {
    const startAt = moment().subtract(1, 'week').add(1, 'day').startOf('day');
    const endAt = moment().endOf('day');
    return !current.isBetween(startAt, endAt, 'day', '[]');
  }, []);

  const fetchHistory = useCallback(
    ({ deviceId, startAt, endAt }) => {
      const encodedStartAt = encodeURIComponent(moment(startAt).format());
      const encodedEndAt = encodeURIComponent(moment(endAt).format());

      commonMethod.requestGetWithToken(
        `dashboard/devices/${deviceId}/report-logs?codes=${
          deviceType === 'SensorMo' ? 'pir' : 'switch'
        }&startAt=${encodedStartAt}&endAt=${encodedEndAt}&valueType=avg&scope=shop`,
        accessToken,
        { props: { history: routeHistory } },
        res => {
          if (!loading) dispatch({ type: 'setLoading', loading: false });
          dispatch({ type: 'setIsRefresh', isRefresh: false });
          dispatch({
            type: 'setHistory',
            history: res.result.list
          });
        },
        err => console.error(err)
      );
    },
    [dispatch, deviceType, accessToken, routeHistory]
  );

  useEffect(() => {
    if (!deviceId || !startAt || !endAt) {
      return;
    }
    dispatch({ type: 'setLoading', loading: true });
    fetchHistory({ deviceId, startAt, endAt });
  }, [dispatch, fetchHistory, deviceId, startAt, endAt]);

  const onDateChange = useCallback(
    ([from, to]) => {
      dispatch({
        type: 'setDate',
        startAt: from.startOf('day').toDate(),
        endAt: to.endOf('day').toDate()
      });
    },
    [dispatch]
  );

  const onRefresh = useCallback(() => {
    dispatch({ type: 'setLoading', loading: true });
    fetchHistory({ deviceId, startAt, endAt });
  }, [dispatch, deviceId, endAt, fetchHistory, startAt]);

  useEffect(() => {
    isRefresh && fetchHistory({ deviceId, startAt, endAt });
  }, [isRefresh, fetchHistory, deviceId, startAt, endAt]);

  const onExport = useCallback(() => {
    const csvExporter = new ExportToCsv({
      fieldSeparator: ',',
      filename: deviceName,
      decimalSeparator: '.',
      showTitle: true,
      title: `${deviceName} 히스토리`,
      useTextFile: false,
      useBom: true,
      headers: [
        '일시',
        `${deviceType === 'SensorDo' ? '움직임 감지' : '열림/닫힘'}`
      ]
    });

    const arr = [];

    // FIXME 더 간결하게
    (historyResolved || []).forEach(item => {
      item.list.forEach(data => {
        if (data.code === 'pir') {
          if (data.value === 'pir') {
            const newHistory = {
              date: moment(data.t).format('YYYY.MM.DD HH:mm'),
              movement: '움직임 감지'
            };
            arr.push({ ...newHistory });
          } else {
            return false;
          }
        }
        if (data.code === 'switch') {
          const newHistory = {
            date: moment(data.t).format('YYYY.MM.DD HH:mm'),
            open: `${data.value ? '열림' : '닫힘'}`
          };
          arr.push({ ...newHistory });
        }
      });
    });
    csvExporter.generateCsv(arr);
  }, [deviceName, deviceType, historyResolved]);

  return (
    <div className="motion-sensor-history">
      <div className="motion-sensor-board">
        <div className="board-header">
          <div className="motion-door-device-name">
            <h3>{deviceName}</h3>
            <small>{deviceType === 'SensorMo' ? '모션센서' : '도어센서'}</small>
          </div>
          <p>Date : </p>
          <RangePicker
            value={[moment(startAt), moment(endAt)]}
            onChange={onDateChange}
            disabledDate={disabledDate}
            format="YYYY.MM.DD"
          />
          {/* FIXME Dropdown으로 변경 */}
          <Popconfirm
            title={'export-refresh-door-motion'}
            placement="bottomLeft"
            onConfirm={onExport}
            onCancel={onRefresh}
            okText="내보내기"
            cancelText="새로고침"
          >
            <Button>
              <svg
                width="4"
                height="16"
                viewBox="0 0 4 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 12C0.895431 12 4.82823e-08 12.8954 0 14C-4.82823e-08 15.1046 0.89543 16 2 16C3.10457 16 4 15.1046 4 14C4 12.8954 3.10457 12 2 12Z"
                  fill="#EAEAEA"
                />
                <path
                  d="M2 6C0.895431 6 3.10551e-07 6.89543 2.62268e-07 8C2.13986e-07 9.10457 0.895431 10 2 10C3.10457 10 4 9.10457 4 8C4 6.89543 3.10457 6 2 6Z"
                  fill="#EAEAEA"
                />
                <path
                  d="M5.24537e-07 2C5.72819e-07 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4C0.895431 4 4.76254e-07 3.10457 5.24537e-07 2Z"
                  fill="#EAEAEA"
                />
              </svg>
            </Button>
          </Popconfirm>
        </div>

        <div className="motion-history-items">
          {loading && (
            <div className="flexible-refresh motion-door">
              <img alt="loading.." src={Logo} />
            </div>
          )}
          {history && history.length === 0 && !loading && (
            <div className="no-motion-door-data">
              <svg
                width={24}
                height={24}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.171 1.255a2 2 0 0 1 1.658 0c.41.186.67.517.85.782.178.263.368.61.579.993l8.334 15.154c.198.358.378.685.499.963.123.284.255.662.203 1.09a2 2 0 0 1-.82 1.388c-.35.252-.746.32-1.054.347-.302.028-.675.028-1.084.028H3.664c-.41 0-.783 0-1.084-.028-.308-.028-.703-.095-1.054-.347a2 2 0 0 1-.82-1.387c-.052-.429.08-.807.203-1.09.121-.279.301-.606.499-.964L9.742 3.03c.211-.384.401-.73.58-.993.179-.265.44-.596.85-.782Zm.808 1.902c-.12.178-.267.441-.505.874L3.18 19.11a11.98 11.98 0 0 0-.45.866l.032.004c.2.018.479.019.944.019h16.588a12.019 12.019 0 0 0 .976-.023 11.934 11.934 0 0 0-.45-.866L12.526 4.03A12.843 12.843 0 0 0 12 3.126l-.021.03ZM11 14V9h2v5h-2Zm0 4v-2h2v2h-2Z"
                  fill="#8A8B8D"
                />
              </svg>
              <p>현재 디바이스에 저장된 히스토리가 없습니다.</p>
            </div>
          )}

          {!loading &&
            historyResolved.length > 0 &&
            historyResolved.map(({ date, list }, index) => {
              return (
                <ul className="items-date" key={index}>
                  <h4>{date}</h4>
                  {history &&
                    history.length > 0 &&
                    list.map((data, index) => (
                      <li key={index}>
                        <p className="item-time">{`${
                          moment(data.t).format('HH') > 11 ? '오후' : '오전'
                        } ${moment(data.t).format('hh : mm : ss')}`}</p>
                        {data.value === 'pir' && (
                          <p className="item-action">움직임 감지</p>
                        )}
                        {data.code === 'switch' && data.value && (
                          <p className="item-door-open">열림</p>
                        )}
                        {data.code === 'switch' && !data.value && (
                          <p className="item-door-close">닫힘</p>
                        )}
                      </li>
                    ))}
                </ul>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default MotionDoorHistory;
